import type { FactionId, FactionTable, King, RunState } from "../types/state";

/**
 * Each kingdom's tables (`FactionTable`): the supply and recruits pools and their spends, and the
 * King. Every write goes through `withFactionValue`, which keeps the table's key order.
 */

/** The table with one kingdom's value set: a new key goes last, an existing one keeps its place. */
export function withFactionValue<Value>(
  table: FactionTable<Value>,
  faction: FactionId,
  value: Value,
): FactionTable<Value> {
  return { ...table, [faction]: value };
}

/** A kingdom's supply pool; 0 for a seat the run does not use. */
export function supplyOf(state: RunState, faction: FactionId): number {
  return state.supply[faction] ?? 0;
}

/** The kingdom's pool moved by `amount`: a refund or a cut is positive, a price negative. */
export function addSupply(state: RunState, faction: FactionId, amount: number): RunState {
  return {
    ...state,
    supply: withFactionValue(state.supply, faction, supplyOf(state, faction) + amount),
  };
}

export function spend(state: RunState, faction: FactionId, amount: number): RunState {
  return addSupply(state, faction, -amount);
}

/** Recruits the faction holds (A53); 0 without the population block. */
export function recruitsOf(state: RunState, faction: FactionId): number {
  return state.population[faction] ?? 0;
}

export function spendRecruits(state: RunState, faction: FactionId, amount: number): RunState {
  if (amount === 0) {
    return state;
  }
  return {
    ...state,
    population: withFactionValue(state.population, faction, recruitsOf(state, faction) - amount),
  };
}

export function kingOf(state: RunState, faction: FactionId): King | undefined {
  return state.kings?.[faction];
}

export function withKing(state: RunState, faction: FactionId, king: King): RunState {
  return state.kings ? { ...state, kings: withFactionValue(state.kings, faction, king) } : state;
}
